import * as Tone from "tone";
import { getPiano, kickAudio, stopAllAudio } from "@/audio/piano";

/**
 * Tight rhythm playback for lessons where timing matters.
 *
 *  • All events are scheduled in TransportTime (BBS strings or note
 *    notation), so they scale automatically with Tone.Transport.bpm.
 *    Change the BPM mid-playback and the metronome AND the events follow
 *    in lockstep.
 *  • Optional audible metronome click on every beat (woodblock-ish sound,
 *    deliberately distinct from the piano).
 *  • Optional `onStep(stepIdx)` callback that's synced to the audio via
 *    Tone.Draw — visual highlights land on the same audio frame.
 *  • Returns a `stop()` you can call to halt the loop early.
 *
 * Cells are described in SUBDIVISION STEPS, not seconds. `subdivide`
 * controls the step-resolution: 1 = quarter, 2 = eighth, 4 = sixteenth,
 * 3 = triplet eighth, etc.
 */

let clickHigh: Tone.MetalSynth | null = null;
let clickLow: Tone.MetalSynth | null = null;
function getClicks() {
  if (!clickHigh) {
    clickHigh = new Tone.MetalSynth({
      envelope: { attack: 0.001, decay: 0.04, release: 0.02 },
      harmonicity: 12,
      modulationIndex: 32,
      resonance: 4000,
      octaves: 0.4,
    }).toDestination();
    clickHigh.volume.value = -16;
  }
  if (!clickLow) {
    clickLow = new Tone.MetalSynth({
      envelope: { attack: 0.001, decay: 0.04, release: 0.02 },
      harmonicity: 8,
      modulationIndex: 24,
      resonance: 2400,
      octaves: 0.4,
    }).toDestination();
    clickLow.volume.value = -19;
  }
  return { clickHigh, clickLow };
}

export interface RhythmEvent {
  /** Position in subdivision steps from the start. */
  beat: number;
  /** Pitches to play at that beat. */
  notes: string[];
  /** Length, expressed in subdivision steps. Defaults to 1. */
  durationSteps?: number;
  velocity?: number;
}

export interface PlayRhythmOptions {
  bpm: number;
  /** How many subdivision steps make one quarter note. 2 = eighth, 4 = sixteenth. */
  subdivide?: number;
  /** Total length in subdivision steps. */
  totalSteps?: number;
  loop?: boolean;
  click?: boolean;
  beatsPerBar?: number;
  onStep?: (stepIdx: number) => void;
}

export interface RhythmHandle {
  stop: () => void;
}

/**
 * Convert a step index + subdivision to a Tone TransportTime string
 * "bars:beats:sixteenths". TransportTime scales with bpm, so events
 * scheduled this way reschedule themselves when the user changes tempo.
 */
function stepToBBS(stepIdx: number, subdivide: number): string {
  // 1 step = (1 / subdivide) of a quarter-note beat.
  const quarters = stepIdx / subdivide;
  const bars = Math.floor(quarters / 4);
  const remainder = quarters - bars * 4;
  const beats = Math.floor(remainder);
  const sixteenths = (remainder - beats) * 4;
  // sixteenths is fractional in the general case; Tone supports decimals here.
  return `${bars}:${beats}:${sixteenths}`;
}

/**
 * Convert a duration in steps to a Tone Time string. Same BBS form, but
 * representing a length rather than an absolute position.
 */
function stepsToDurationBBS(steps: number, subdivide: number): string {
  const quarters = steps / subdivide;
  const bars = Math.floor(quarters / 4);
  const remainder = quarters - bars * 4;
  const beats = Math.floor(remainder);
  const sixteenths = (remainder - beats) * 4;
  return `${bars}:${beats}:${sixteenths}`;
}

export async function playRhythm(
  events: RhythmEvent[],
  opts: PlayRhythmOptions
): Promise<RhythmHandle> {
  kickAudio();
  stopAllAudio();
  const piano = await getPiano();

  const subdivide = opts.subdivide ?? 1;
  const beatsPerBar = opts.beatsPerBar ?? 4;
  const totalSteps =
    opts.totalSteps ??
    events.reduce(
      (max, ev) => Math.max(max, ev.beat + (ev.durationSteps ?? 1)),
      0
    );

  const t = Tone.getTransport();
  t.cancel();
  t.bpm.value = opts.bpm;

  const scheduledIds: number[] = [];

  // Note events — schedule in TRANSPORT-TIME so they scale with BPM
  for (const ev of events) {
    const id = t.schedule((time) => {
      const durBBS = stepsToDurationBBS(ev.durationSteps ?? 1, subdivide);
      // Multiply duration slightly under-step to avoid overlapping ties
      piano.triggerAttackRelease(ev.notes, durBBS, time, ev.velocity ?? 0.85);
    }, stepToBBS(ev.beat, subdivide));
    scheduledIds.push(id);
  }

  // Audible metronome click on every beat — beat-relative, scales with BPM
  if (opts.click) {
    const { clickHigh, clickLow } = getClicks();
    const clickId = t.scheduleRepeat((time) => {
      // Derive the beat from the transport position (loop-aware) instead of a
      // monotonic counter: a counter drifts the downbeat accent from loop 2
      // onward whenever the cell isn't a whole number of bars.
      const beat = Math.floor(t.getTicksAtTime(time) / t.PPQ);
      const isDown = beat % beatsPerBar === 0;
      // Higher pitch on downbeats, lower on offbeats — woodblock metronome feel
      if (isDown) {
        clickHigh.triggerAttackRelease("C7", "32n", time);
      } else {
        clickLow.triggerAttackRelease("G6", "32n", time);
      }
    }, "4n");
    scheduledIds.push(clickId);
  }

  // Visual onStep — also beat-relative
  if (opts.onStep) {
    for (let s = 0; s < totalSteps; s++) {
      const id = t.schedule((time) => {
        Tone.Draw.schedule(() => opts.onStep && opts.onStep(s), time);
      }, stepToBBS(s, subdivide));
      scheduledIds.push(id);
    }
  }

  // Loop or stop on end
  const loopEndBBS = stepsToDurationBBS(totalSteps, subdivide);
  if (opts.loop) {
    t.loop = true;
    t.loopStart = 0;
    t.loopEnd = loopEndBBS;
  } else {
    t.loop = false;
  }

  t.position = "0:0:0";
  t.start("+0.05");

  return {
    stop: () => {
      t.stop();
      t.cancel();
      scheduledIds.forEach((id) => t.clear(id));
    },
  };
}

/** Live tempo update — call this when the user moves a BPM slider mid-playback.
 *  Smoothly ramps Tone.Transport.bpm without restarting the loop. */
export function setRhythmBpm(bpm: number, rampSeconds = 0.1): void {
  try {
    Tone.getTransport().bpm.rampTo(bpm, rampSeconds);
  } catch {
    /* transport not started — ignore */
  }
}
